// AdminWorkspaceDetail.tsx — /admin/workspaces/:id (Task 3 of the admin-v2 cycle, spec
// docs/superpowers/specs/2026-08-06-admin-v2-internal-workspaces-design.md §B1). Facts header
// (name, owner email+role+banned badge, plan or INTERNAL chip, created), usage block, bots
// list (links to bot detail), a read-only members table (owner first then joined-ascending,
// workspace-logo-and-admin-members cycle §B — no admin-side role changes, owners manage their
// own members via /members), subscription block ("Manage in Polar ↗" is a label-only external
// link — no org slug guessing, per the task brief), and a danger zone: internal toggle,
// ban/unban, role select. Every mutation here is audit-logged server-side (routes/admin.ts) —
// this screen just needs confirm dialogs + inline error surfaces, never a silent failure (the
// billing-UX lesson carried over from Billing.tsx).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { PLAN_LIMITS } from '@repo/shared-types';
import type {
  AdminUserActionDto,
  AdminWorkspaceDetailDto,
  AdminWorkspaceInternalDto,
  MemberRole,
} from '@repo/shared-types';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { Card } from '@repo/ui/components/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { Switch } from '@repo/ui/components/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/table';
import { useSession } from '../lib/auth-client';
import { apiFetch, ApiError } from '../lib/api';
import { useLocale } from '../locales/context';
import type { Catalog } from '../locales/en';
import { formatDate as formatDateIntl, formatNumber } from '../locales/format';
import type { Locale } from '../locales';

const POLAR_DASHBOARD_URL = 'https://polar.sh/dashboard';

/** Same idiom as `Members.tsx`'s `RoleChip` — plain muted span, not `StatusChip` (role isn't a
 * status). Kept as its own copy rather than a shared import: `Members.tsx`'s version isn't
 * exported, and this screen's members table is read-only (no role `<select>` to share styling
 * with either). */
function MemberRoleChip({ role, t }: { role: MemberRole; t: Catalog }) {
  return (
    <span className="inline-flex w-fit items-center rounded-full bg-muted px-2.5 py-[3px] text-xs font-medium text-muted-foreground">
      {t.admin.workspaces.memberRoleLabel[role]}
    </span>
  );
}

function queryKey(id: string) {
  return ['admin', 'workspace', id] as const;
}

/** Sized to the real facts header + usage card below (handoff Shell Anatomy §06). */
function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <SkeletonText className="w-32" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-8 w-56" />
        <SkeletonText className="w-72" />
      </div>
      <Card className="gap-3 p-5">
        <SkeletonText className="w-16" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="flex flex-col gap-1.5">
              <SkeletonText className="w-24" />
              <SkeletonText className="w-16" />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function formatDate(iso: string, locale: Locale): string {
  return formatDateIntl(iso, locale, { month: 'long', day: 'numeric', year: 'numeric' });
}

export function AdminWorkspaceDetail() {
  const { t, locale } = useLocale();
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { data: session } = useSession();

  const { data: workspace, isPending, error } = useQuery({
    queryKey: queryKey(id),
    queryFn: () => apiFetch<AdminWorkspaceDetailDto>(`/api/admin/workspaces/${id}`),
  });

  // ── Internal toggle ──────────────────────────────────────────────────────────────────
  const [internalDialogTarget, setInternalDialogTarget] = useState<boolean | null>(null);
  const [internalError, setInternalError] = useState<string | null>(null);
  const internalMutation = useMutation({
    mutationFn: (internal: boolean) =>
      apiFetch<AdminWorkspaceInternalDto>(`/api/admin/workspaces/${id}/internal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ internal }),
      }),
    onSuccess: () => {
      setInternalDialogTarget(null);
      void queryClient.invalidateQueries({ queryKey: queryKey(id) });
    },
    onError: (err) =>
      setInternalError(
        err instanceof ApiError ? err.message : t.admin.workspaces.updateWorkspaceErrorFallback,
      ),
  });

  // ── Ban / unban ──────────────────────────────────────────────────────────────────────
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [banError, setBanError] = useState<string | null>(null);
  const banMutation = useMutation({
    mutationFn: (reason: string) =>
      apiFetch<AdminUserActionDto>(`/api/admin/users/${workspace?.owner?.userId}/ban`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reason.trim() ? { reason: reason.trim() } : {}),
      }),
    onSuccess: () => {
      setBanDialogOpen(false);
      setBanReason('');
      void queryClient.invalidateQueries({ queryKey: queryKey(id) });
    },
    onError: (err) =>
      setBanError(err instanceof ApiError ? err.message : t.admin.workspaces.banUserErrorFallback),
  });

  const [unbanError, setUnbanError] = useState<string | null>(null);
  const unbanMutation = useMutation({
    mutationFn: () =>
      apiFetch<AdminUserActionDto>(`/api/admin/users/${workspace?.owner?.userId}/unban`, {
        method: 'POST',
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKey(id) }),
    onError: (err) =>
      setUnbanError(
        err instanceof ApiError ? err.message : t.admin.workspaces.unbanUserErrorFallback,
      ),
  });

  // ── Role change ──────────────────────────────────────────────────────────────────────
  const [roleDialogTarget, setRoleDialogTarget] = useState<'user' | 'admin' | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);
  const roleMutation = useMutation({
    mutationFn: (role: 'user' | 'admin') =>
      apiFetch<AdminUserActionDto>(`/api/admin/users/${workspace?.owner?.userId}/role`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      }),
    onSuccess: () => {
      setRoleDialogTarget(null);
      void queryClient.invalidateQueries({ queryKey: queryKey(id) });
    },
    onError: (err) =>
      setRoleError(
        err instanceof ApiError ? err.message : t.admin.workspaces.changeRoleErrorFallback,
      ),
  });

  if (isPending) return <DetailSkeleton />;
  if (error) return <p className="text-destructive">{error.message}</p>;
  if (!workspace) return null;

  const caps = PLAN_LIMITS[workspace.plan];
  const owner = workspace.owner;
  const isSelf = owner !== null && session?.user.id === owner.userId;
  const ownerIsAdmin = owner?.role === 'admin';

  return (
    <div className="flex flex-col gap-6">
      <Link to="/admin" className="w-fit text-sm text-muted-foreground hover:text-foreground">
        {t.admin.workspaces.backToWorkspaces}
      </Link>

      {/* Facts header */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-display text-2xl font-bold">{workspace.name}</h1>
          {workspace.internal ? (
            <Badge>{t.admin.workspaces.internalBadge}</Badge>
          ) : (
            <Badge variant="secondary">{caps.name}</Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span>
            {t.admin.workspaces.ownerLabel}
            {owner ? (
              <>
                {owner.email}{' '}
                <Badge variant="outline" className="ml-1">
                  {owner.role ?? 'user'}
                </Badge>
                {owner.banned && (
                  <Badge variant="destructive" className="ml-1">
                    {t.admin.workspaces.bannedBadge}
                  </Badge>
                )}
              </>
            ) : (
              '—'
            )}
          </span>
          <span>{t.admin.workspaces.createdLabel({ date: formatDate(workspace.createdAt, locale) })}</span>
        </div>
      </div>

      {/* Usage block */}
      <Card className="gap-3 p-5">
        <h2 className="text-sm font-semibold text-muted-foreground">{t.admin.workspaces.usageHeading}</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">{t.admin.workspaces.messagesThisMonthLabel}</span>
            <span className="font-medium">
              {formatNumber(workspace.usage.messagesThisMonth, locale)}
              {!workspace.internal && ` / ${formatNumber(caps.maxMessages, locale)}`}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">{t.admin.workspaces.pagesIndexedLabel}</span>
            <span className="font-medium">
              {formatNumber(workspace.usage.pagesTotal, locale)}
              {!workspace.internal && ` / ${formatNumber(caps.maxPages, locale)}`}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">{t.admin.workspaces.botsLabel}</span>
            <span className="font-medium">
              {formatNumber(workspace.usage.botsCount, locale)}
              {!workspace.internal && ` / ${formatNumber(caps.maxBots, locale)}`}
            </span>
          </div>
        </div>
        {workspace.internal && (
          <p className="text-xs text-muted-foreground">{t.admin.workspaces.internalUsageNote}</p>
        )}
      </Card>

      {/* Bots list */}
      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-muted-foreground">{t.admin.workspaces.botsHeading}</h2>
        {workspace.bots.length === 0 ? (
          <p className="text-muted-foreground">{t.admin.workspaces.noBotsYet}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.admin.workspaces.colBotName}</TableHead>
                <TableHead>{t.admin.workspaces.colPublicId}</TableHead>
                <TableHead>{t.admin.workspaces.colStatus}</TableHead>
                <TableHead>{t.admin.workspaces.colBotCreated}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {workspace.bots.map((bot) => (
                <TableRow key={bot.id}>
                  <TableCell className="font-medium">
                    <Link to={`/admin/bots/${bot.id}`} className="hover:underline">
                      {bot.name}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{bot.publicId}</TableCell>
                  <TableCell>{bot.status}</TableCell>
                  <TableCell>{formatDate(bot.createdAt, locale)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Members list */}
      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-muted-foreground">{t.admin.workspaces.membersHeading}</h2>
        {workspace.members.length === 0 ? (
          <p className="text-muted-foreground">{t.admin.workspaces.noMembersYet}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.admin.workspaces.colEmail}</TableHead>
                <TableHead>{t.admin.workspaces.colMemberName}</TableHead>
                <TableHead>{t.admin.workspaces.colRole}</TableHead>
                <TableHead>{t.admin.workspaces.colJoined}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {workspace.members.map((member) => (
                <TableRow key={member.userId}>
                  <TableCell className="font-medium">{member.email}</TableCell>
                  <TableCell>{member.name ?? '—'}</TableCell>
                  <TableCell>
                    <MemberRoleChip role={member.role} t={t} />
                  </TableCell>
                  <TableCell>{formatDate(member.joinedAt, locale)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Subscription block */}
      <Card className="gap-3 p-5">
        <h2 className="text-sm font-semibold text-muted-foreground">{t.admin.workspaces.subscriptionHeading}</h2>
        {workspace.subscription ? (
          <div className="flex flex-col gap-1.5 text-sm">
            <span>
              {t.admin.workspaces.statusLabel}
              <span className="font-medium">{workspace.subscription.status ?? '—'}</span>
            </span>
            <span>
              {t.admin.workspaces.periodEndLabel}
              <span className="font-medium">
                {workspace.subscription.periodEnd
                  ? formatDate(workspace.subscription.periodEnd, locale)
                  : '—'}
              </span>
            </span>
            <span>
              {t.admin.workspaces.cancelsAtPeriodEndLabel}
              <span className="font-medium">
                {workspace.subscription.cancelAtPeriodEnd
                  ? t.admin.workspaces.yesLabel
                  : t.admin.workspaces.noLabel}
              </span>
            </span>
            <span>
              {t.admin.workspaces.polarSubscriptionIdLabel}
              <span className="font-mono text-xs">{workspace.subscription.polarSubscriptionId}</span>
            </span>
            <a
              href={POLAR_DASHBOARD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-fit text-[13px] text-primary hover:underline"
            >
              {t.admin.workspaces.manageInPolarLink}
            </a>
          </div>
        ) : (
          <p className="text-muted-foreground">{t.admin.workspaces.noSubscriptionText}</p>
        )}
      </Card>

      {/* Danger zone */}
      <Card className="gap-4 border-destructive/40 p-5">
        <h2 className="text-sm font-semibold text-destructive">{t.admin.workspaces.dangerZoneHeading}</h2>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col">
              <span className="text-sm font-medium">{t.admin.workspaces.internalToggleLabel}</span>
              <span className="text-xs text-muted-foreground">{t.admin.workspaces.internalToggleHint}</span>
            </div>
            <Switch
              checked={workspace.internal}
              disabled={internalMutation.isPending}
              onCheckedChange={(checked) => {
                setInternalError(null);
                setInternalDialogTarget(checked);
              }}
            />
          </div>
          {internalError && <p className="text-[13px] text-destructive">{internalError}</p>}
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col">
              <span className="text-sm font-medium">{t.admin.workspaces.banOwnerLabel}</span>
              <span className="text-xs text-muted-foreground">{t.admin.workspaces.banOwnerHint}</span>
            </div>
            {owner?.banned ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={unbanMutation.isPending || !owner}
                onClick={() => {
                  setUnbanError(null);
                  unbanMutation.mutate();
                }}
              >
                {unbanMutation.isPending ? t.admin.workspaces.unbanningCta : t.admin.workspaces.unbanCta}
              </Button>
            ) : (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={!owner || ownerIsAdmin}
                title={ownerIsAdmin ? t.admin.workspaces.demoteBeforeBanTitle : undefined}
                onClick={() => {
                  setBanError(null);
                  setBanDialogOpen(true);
                }}
              >
                {t.admin.workspaces.banCta}
              </Button>
            )}
          </div>
          {unbanError && <p className="text-[13px] text-destructive">{unbanError}</p>}
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col">
              <span className="text-sm font-medium">{t.admin.workspaces.roleLabel}</span>
              <span className="text-xs text-muted-foreground">{t.admin.workspaces.roleHint}</span>
            </div>
            <Select
              value={owner?.role ?? 'user'}
              disabled={!owner || isSelf || roleMutation.isPending}
              onValueChange={(value) => {
                setRoleError(null);
                setRoleDialogTarget(value as 'user' | 'admin');
              }}
            >
              <SelectTrigger
                size="sm"
                title={isSelf ? t.admin.workspaces.cannotChangeOwnRoleTitle : undefined}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">user</SelectItem>
                <SelectItem value="admin">admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {roleError && <p className="text-[13px] text-destructive">{roleError}</p>}
        </div>
      </Card>

      {/* Internal toggle confirm dialog */}
      <Dialog
        open={internalDialogTarget !== null}
        onOpenChange={(open) => !open && setInternalDialogTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {internalDialogTarget
                ? t.admin.workspaces.markInternalDialogTitle
                : t.admin.workspaces.removeInternalDialogTitle}
            </DialogTitle>
            <DialogDescription>
              {internalDialogTarget
                ? t.admin.workspaces.markInternalDialogDescription
                : t.admin.workspaces.removeInternalDialogDescription}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setInternalDialogTarget(null)}>
              {t.admin.workspaces.cancelCta}
            </Button>
            <Button
              type="button"
              disabled={internalMutation.isPending}
              onClick={() => internalDialogTarget !== null && internalMutation.mutate(internalDialogTarget)}
            >
              {internalMutation.isPending ? t.admin.workspaces.savingCta : t.admin.workspaces.confirmCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ban confirm dialog */}
      <Dialog open={banDialogOpen} onOpenChange={setBanDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.admin.workspaces.banDialogTitle({ email: owner?.email })}</DialogTitle>
            <DialogDescription>{t.admin.workspaces.banDialogDescription}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="ban-reason" className="text-xs text-muted-foreground">
              {t.admin.workspaces.banReasonLabel}
            </label>
            <textarea
              id="ban-reason"
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              maxLength={500}
              rows={3}
              className="rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              placeholder={t.admin.workspaces.banReasonPlaceholder}
            />
          </div>
          {banError && <p className="text-[13px] text-destructive">{banError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setBanDialogOpen(false)}>
              {t.admin.workspaces.cancelCta}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={banMutation.isPending}
              onClick={() => banMutation.mutate(banReason)}
            >
              {banMutation.isPending ? t.admin.workspaces.banningCta : t.admin.workspaces.banUserCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role change confirm dialog */}
      <Dialog
        open={roleDialogTarget !== null}
        onOpenChange={(open) => !open && setRoleDialogTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.admin.workspaces.roleDialogTitle({ role: roleDialogTarget })}</DialogTitle>
            <DialogDescription>
              {roleDialogTarget === 'admin'
                ? t.admin.workspaces.roleDialogDescriptionAdmin({ email: owner?.email })
                : t.admin.workspaces.roleDialogDescriptionUser({ email: owner?.email })}
            </DialogDescription>
          </DialogHeader>
          {roleError && <p className="text-[13px] text-destructive">{roleError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRoleDialogTarget(null)}>
              {t.admin.workspaces.cancelCta}
            </Button>
            <Button
              type="button"
              disabled={roleMutation.isPending}
              onClick={() => roleDialogTarget !== null && roleMutation.mutate(roleDialogTarget)}
            >
              {roleMutation.isPending ? t.admin.workspaces.savingCta : t.admin.workspaces.confirmCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
